/** 区划选项（`GET /api/sys/region/options`）：只含代码与名称，不含经纬度。 */
export interface RegionOption {
  regionCode: string;
  parentCode: string;
  regionName: string;
  regionLevel: number;
}

/** 级联选择节点（与 antd Cascader 的 options 结构一致）。 */
export interface RegionCascadeNode {
  value: string;
  label: string;
  isLeaf?: boolean;
  children?: RegionCascadeNode[];
}

/** 平铺区划 → 省/市/区三级级联树；父级缺失的节点降级为根，保证不丢数据。 */
export function buildRegionCascade(options: RegionOption[]): RegionCascadeNode[] {
  const nodes = new Map<string, RegionCascadeNode>();
  for (const item of options) {
    nodes.set(item.regionCode, {
      value: item.regionCode,
      label: item.regionName,
      isLeaf: item.regionLevel >= 3
    });
  }

  const roots: RegionCascadeNode[] = [];
  for (const item of options) {
    const node = nodes.get(item.regionCode)!;
    const parent =
      item.regionLevel > 1 && item.parentCode && item.parentCode !== '0'
        ? nodes.get(item.parentCode)
        : undefined;
    if (parent) {
      parent.children = [...(parent.children ?? []), node];
      // 有下级说明不是叶子，避免 Cascader 提前终止展开
      parent.isLeaf = false;
    } else {
      roots.push(node);
    }
  }

  return roots;
}

/** 按末级代码回溯 [省, 市, 区] 代码；找不到的层级跳过。 */
export function findRegionPath(options: RegionOption[], districtCode?: string | null): string[] {
  if (!districtCode) return [];
  const byCode = new Map(options.map((item) => [item.regionCode, item]));
  const path: string[] = [];
  let cursor: string | undefined = districtCode;

  for (let depth = 0; depth < 4 && cursor; depth++) {
    const node = byCode.get(cursor);
    if (!node) break;
    path.unshift(node.regionCode);
    cursor = node.parentCode && node.parentCode !== '0' ? node.parentCode : undefined;
  }

  return path;
}

/** 区划代码 → 「浙江省 / 杭州市 / 富阳区」文本（缺失时回退为代码）。 */
export function regionLabel(options: RegionOption[], codes: (string | null | undefined)[]): string {
  const byCode = new Map(options.map((item) => [item.regionCode, item.regionName]));
  return codes
    .filter((code): code is string => Boolean(code))
    .map((code) => byCode.get(code) ?? code)
    .join(' / ');
}

/** 简称口径：2–6 个纯汉字（不含字母、数字、标点），前后端唯一口径。 */
export const SHORT_NAME_PATTERN = /^[\u4e00-\u9fa5]{2,6}$/;

/** 简称校验提示（前后端同一口径）。 */
export const SHORT_NAME_HINT = '简称需 2–6 个汉字（不含字母、数字、标点）';

/** 返回错误信息；合法时返回 null。 */
export function checkShortName(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  if (!text) return '请填写简称';
  return SHORT_NAME_PATTERN.test(text) ? null : SHORT_NAME_HINT;
}

/** `public/china-regions.json` 节点（结构见该文件：province/city/district 三级）。 */
export interface ChinaRegionNodeLike {
  id?: string;
  name: string;
  code: string;
  level: string;
  children?: ChinaRegionNodeLike[];
}

/** 区划层级中文 → sys_region.region_level（1 省 / 2 市 / 3 区县）。 */
export const CHINA_REGION_LEVEL: Record<string, number> = {
  province: 1,
  city: 2,
  district: 3
};

export interface FlatRegionItem {
  code: string;
  name: string;
  level: number;
  parentCode: string;
}

/** 递归平铺全国区划树（stdin: 省数组）。 */
export function flattenChinaRegions(nodes: ChinaRegionNodeLike[], parentCode = '0'): FlatRegionItem[] {
  const result: FlatRegionItem[] = [];
  for (const node of nodes) {
    const level = CHINA_REGION_LEVEL[node.level] ?? 3;
    const code = node.code || node.id || '';
    if (!code) continue;
    result.push({ code, name: node.name, level, parentCode });
    if (node.children?.length) result.push(...flattenChinaRegions(node.children, code));
  }
  return result;
}
